import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import NonAuthLayout from "../../Layout/NonAuthLayout";
import {
  Save,
  ArrowLeft,
  Loader2,
  Image as ImageIcon,
} from "lucide-react";
import {
  AddEnquiryThunk,
  GetSingleEnquiryThunk,
  UpdateEnquiryThunk,
} from "../../redux/states/enquiries/thunk";
import { AllProductsThunk } from "../../redux/states/products/thunk";

const CreateUpdateEnquiries = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { uuid } = useParams();

  // Redux states
  const { singleEnquiry, loading, error } = useSelector(
    (state) => state.enquiries
  );
  const { products } = useSelector((state) => state.products);

  const [formData, setFormData] = useState({
    product_id: "",
    name: "",
    email: "",
    phone: "",
    message: "",
    quantity: "",
    preferred_unit: "",
    price_expectation: "",
    work_status: "new",
    status: "active",
  });

  const [isUpdating, setIsUpdating] = useState(false);

  // ✅ Load enquiry for edit
  useEffect(() => {
    if (uuid) {
      setIsUpdating(true);
      dispatch(GetSingleEnquiryThunk(uuid));
    }
  }, [uuid, dispatch]);

  // ✅ Load all products for dropdown
  useEffect(() => {
    dispatch(AllProductsThunk());
  }, [dispatch]);

  // ✅ Prefill form when fetched (for edit)
  useEffect(() => {
    if (singleEnquiry && isUpdating) {
      setFormData({
        product_id: singleEnquiry.product_id || "",
        name: singleEnquiry.name || "",
        email: singleEnquiry.email || "",
        phone: singleEnquiry.phone || "",
        message: singleEnquiry.message || "",
        quantity: singleEnquiry.quantity || "",
        preferred_unit: singleEnquiry.preferred_unit || "",
        price_expectation: singleEnquiry.price_expectation || "",
        work_status: singleEnquiry.work_status || "new",
        status: singleEnquiry.status || "active",
      });
    }
  }, [singleEnquiry, isUpdating]);

  // ✅ Input change handler
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // ✅ Submit handler
  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      alert("Please enter name");
      return;
    }

    if (!formData.product_id) {
      alert("Please select a product");
      return;
    }

    if (isUpdating) {
      const payload = { ...formData, uuid }; // ✅ include uuid in body
      dispatch(UpdateEnquiryThunk(payload)).then(() => {
        navigate("/enquiries");
      });
    } else {
      dispatch(AddEnquiryThunk(formData)).then(() => {
        navigate("/enquiries");
      });
    }
  };

  return (
    <NonAuthLayout>
      <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="bg-white rounded-2xl shadow-sm border p-6 mb-8 flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">
                {isUpdating ? "Update Enquiry" : "Add New Enquiry"}
              </h1>
              <p className="text-gray-600 mt-1">
                {isUpdating
                  ? "Edit enquiry details below."
                  : "Fill the form to create a new enquiry."}
              </p>
            </div>
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-gray-600 hover:text-blue-600 border border-gray-300 px-4 py-2 rounded-lg transition"
            >
              <ArrowLeft className="w-4 h-4" />
              Go Back
            </button>
          </div>

          {/* Error */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 mb-6">
              {error}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Enquiry Details */}
            <div className="bg-white rounded-2xl shadow-sm border p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">
                Enquiry Details
              </h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {/* Product Dropdown */}
                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Select Product *
                  </label>
                  <select
                    name="product_id"
                    value={formData.product_id}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">-- Select Product --</option>
                    {products?.map((prod) => (
                      <option key={prod.id} value={prod.id}>
                        {prod.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Name */}
                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter name"
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Enter email"
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Phone
                  </label>
                  <input
                    type="text"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="Enter phone number"
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Quantity */}
                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Quantity
                  </label>
                  <input
                    type="number"
                    name="quantity"
                    value={formData.quantity}
                    onChange={handleChange}
                    placeholder="Enter quantity"
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Preferred Unit */}
                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Preferred Unit *
                  </label>
                  <select
                    name="preferred_unit"
                    value={formData.preferred_unit}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">-- Select Unit --</option>
                    {Array.from(
                      new Set(products?.map((prod) => prod.unit_type))
                    ).map((unit) => (
                      <option key={unit} value={unit}>
                        {unit}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Price Expectation */}
                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Price Expectation
                  </label>
                  <input
                    type="text"
                    name="price_expectation"
                    value={formData.price_expectation}
                    onChange={handleChange}
                    placeholder="Enter expected price"
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Work Status */}
                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Work Status
                  </label>
                  <select
                    name="work_status"
                    value={formData.work_status}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="new">New</option>
                    <option value="processing">Processing</option>
                    <option value="done">Done</option>
                  </select>
                </div>

                {/* Status */}
                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Status
                  </label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>

                {/* Message */}
                <div className="sm:col-span-2">
                  <label className="block text-gray-700 font-medium mb-2">
                    Message
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows="4"
                    placeholder="Enter message..."
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500"
                  ></textarea>
                </div>
              </div>
            </div>

            {/* Submit */}
            <div className="bg-white rounded-2xl shadow-sm border p-6">
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center items-center gap-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 px-6 rounded-xl transition-all duration-300 transform hover:scale-105 disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {isUpdating ? "Updating..." : "Saving..."}
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5" />
                    {isUpdating ? "Update Enquiry" : "Save Enquiry"}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </NonAuthLayout>
  );
};

export default CreateUpdateEnquiries;
